'use client';

import { useSettleInsuranceFundRevenue } from 'src/hooks/driftActions/settlement/useSettleInsuranceFundRevenue';
import useMarketsInfoStore from 'src/stores/useMarketsInfoStore';
import { toFixedLocaleString } from 'src/utils/formatting';
import { findDaysHoursMinutesFromNow } from 'src/utils/dateTime';
import { format } from 'timeago.js';
import { useIfDataQuery } from 'src/hooks/query/ifData/useIfDataQuery';
import { ReactNode, useMemo } from 'react';
import useModal from 'src/components/Modals/hooks/useModal';
import { SpotMarketConfig } from '@drift-labs/sdk';
import { cj } from 'src/utils/classNames';
import ResponsiveModal from 'src/components/Modals/ResponsiveModal/ResponsiveModal';
import Button from '../primitives/Button/Button';

export interface InsuranceFundDetailsModalProps {
	selectedIfStakeMarket: SpotMarketConfig;
}

const ModalContent = ({
	selectedIfStakeMarket,
}: InsuranceFundDetailsModalProps) => {
	const { settleInsuranceFundRevenue } = useSettleInsuranceFundRevenue();
	const { data: ifData } = useIfDataQuery(
		selectedIfStakeMarket.marketIndex as number
	);
	const displayDecimals =
		useMarketsInfoStore(
			(s) =>
				s.allMarketsInfo?.spotLookup?.[selectedIfStakeMarket.marketIndex]
					?.genericInfo?.baseDisplayDecimals
		) ?? 2;

	const vaultAccountString = useMemo(() => {
		return ifData?.vault.account?.vault?.toString();
	}, [ifData?.vault.account]);

	const handleSettleRevenuePool = () => {
		settleInsuranceFundRevenue(selectedIfStakeMarket.marketIndex);
	};

	if (!ifData) {
		return null;
	}

	const {
		totalStake = 0,
		revenuePool = 0,
		payoutRatio = 0,
		ratioForStakers = 0,
		nextPayoutTotal = 0,
		revenueSettlePeriodMinutes = 0,
		secondsUntilNextRevenueSettle = 0,
		lastRevenueSettleMs = 0,
		maxApr = 0,
	} = ifData.vault;

	const secondsUntilNextRevenueSettleDisplay = findDaysHoursMinutesFromNow(
		new Date(Date.now() + secondsUntilNextRevenueSettle * 1000)
	);

	const ratioForProtocol = 1 - ratioForStakers;

	return (
		<>
			<DetailsRow fullWidth={true}>
				<span className="typo-b4">Vault Account</span>
				<span className="typo-b4">
					<a
						href={`https://solscan.io/account/${vaultAccountString}`}
						target="_blank"
						rel="noreferrer"
					>
						View on Solscan
					</a>
				</span>
			</DetailsRow>
			<DetailsRow fullWidth={true}>
				<span className="typo-b4">Current Vault Balance</span>
				<span className="text-right typo-b4">
					{toFixedLocaleString(totalStake, displayDecimals)}{' '}
					{selectedIfStakeMarket.symbol}
				</span>
			</DetailsRow>
			<DetailsRow fullWidth={true}>
				<span className="typo-b4">Current Revenue Pool</span>
				<span className="text-right typo-b4">
					{toFixedLocaleString(revenuePool, displayDecimals)}{' '}
					{selectedIfStakeMarket.symbol}
				</span>
			</DetailsRow>
			<DetailsRow fullWidth={true}>
				<span className="typo-b4">Payout Per Settle</span>
				<span className="text-right typo-b4">{payoutRatio * 100}%</span>
			</DetailsRow>
			<DetailsRow fullWidth={true}>
				<span className="typo-b4">Share of Payout</span>
				<span className="text-right typo-b4">
					{ratioForStakers * 100}% (stakers) / {ratioForProtocol * 100}%
					(protocol)
				</span>
			</DetailsRow>
			<DetailsRow fullWidth={true}>
				<span className="typo-b4 whitespace-nowrap">Next Payout</span>
				<span className="text-right typo-b4">
					{toFixedLocaleString(
						ratioForStakers * nextPayoutTotal,
						displayDecimals
					)}{' '}
					{selectedIfStakeMarket.symbol} (stakers) /{' '}
					{toFixedLocaleString(
						ratioForProtocol * nextPayoutTotal,
						displayDecimals
					)}{' '}
					{selectedIfStakeMarket.symbol} (protocol)
				</span>
			</DetailsRow>
			<DetailsRow fullWidth={true}>
				<span className="typo-b4">APR Cap</span>
				<span className="text-right typo-b4">
					{ratioForStakers * maxApr}% (stakers) / {ratioForProtocol * maxApr}%
					(protocol)
				</span>
			</DetailsRow>
			<DetailsRow fullWidth={true}>
				<span className="typo-b4">Revenue Settle Period</span>
				<span className="text-right typo-b4">
					{revenueSettlePeriodMinutes}m
				</span>
			</DetailsRow>
			{/* We could put the "settle now" button in this row, if it has been >= 1 hour */}
			<DetailsRow fullWidth={true}>
				<span className="typo-b4">Last Revenue Settle</span>
				<span className="text-right typo-b4">
					{format(new Date(lastRevenueSettleMs))}
				</span>
			</DetailsRow>
			<DetailsRow fullWidth={true}>
				<span className="typo-b4">Next Revenue Settle</span>
				<span className="text-right typo-b4">
					{secondsUntilNextRevenueSettleDisplay === 'now'
						? 'now'
						: secondsUntilNextRevenueSettleDisplay + ' from now'}
				</span>
			</DetailsRow>
			<div className="mt-4 w-full">
				<Button
					size="lg"
					variant="primary"
					className="w-full"
					// Time until next settle should come from the sdk once the function has been merged
					disabled={secondsUntilNextRevenueSettle > 0}
					onClick={handleSettleRevenuePool}
				>
					Settle Revenue Pool
				</Button>
			</div>
		</>
	);
};

const InsuranceFundDetailsModal = ({
	selectedIfStakeMarket,
}: InsuranceFundDetailsModalProps) => {
	const { hide } = useModal();

	return (
		<ResponsiveModal.Container
			onClose={hide}
			dataTestId="insurance-fund-details-modal"
		>
			<ResponsiveModal.Title closeButtonTestId="insurance-fund-details-modal-close">
				{selectedIfStakeMarket.symbol} Insurance Fund Vault Details
			</ResponsiveModal.Title>
			<ResponsiveModal.Body>
				<ModalContent selectedIfStakeMarket={selectedIfStakeMarket} />
			</ResponsiveModal.Body>
		</ResponsiveModal.Container>
	);
};

export const DetailsRow = (props: {
	children: ReactNode;
	fullWidth?: boolean;
}) => (
	<div
		className={cj(
			'flex min-w-[200px] items-start justify-between space-x-4',
			props.fullWidth && 'w-full'
		)}
	>
		{props.children}
	</div>
);

export default InsuranceFundDetailsModal;
